import type { GeneratedMeta, PortfolioSnapshot, Skill } from "./types";

export async function fetchLiveMeta(staticMeta: GeneratedMeta): Promise<GeneratedMeta> {
  try {
    const res = await fetch("/api/meta", { cache: "no-store" });
    if (!res.ok) return { ...staticMeta, dataSource: "static" };
    const live = (await res.json()) as GeneratedMeta & { error?: string };
    if (live.error) return { ...staticMeta, dataSource: "static" };
    const isNewerThanStatic = Boolean(
      live.generated && staticMeta.generated && live.generated > staticMeta.generated
    );
    return {
      ...staticMeta,
      ...live,
      dataSource: live.dataSource ?? "github",
      isNewerThanStatic,
    };
  } catch {
    return { ...staticMeta, dataSource: "static" };
  }
}

export interface LlamaStatus {
  ok: boolean;
  provider?: string;
  host?: string;
  modelCount?: number;
  models?: string[];
  defaultModel?: string;
  error?: string;
}

/** @deprecated Use LlamaStatus */
export type OllamaStatus = LlamaStatus;

export interface ApiHealth {
  ok: boolean;
  actionsReady?: boolean;
  endpoints?: string[];
  error?: string;
}

export async function fetchApiHealth(): Promise<ApiHealth | null> {
  try {
    const res = await fetch("/api/health", { cache: "no-store" });
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
    return (await res.json()) as ApiHealth;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "unavailable" };
  }
}

export async function fetchLlamaStatus(): Promise<LlamaStatus | null> {
  try {
    const res = await fetch("/api/llama", { cache: "no-store" });
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
    return (await res.json()) as LlamaStatus;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "unavailable" };
  }
}

/** @deprecated Use fetchLlamaStatus */
export const fetchOllamaStatus = fetchLlamaStatus;

export interface LiveScoresPayload {
  generated: string | null;
  count: number;
  skills: Skill[];
  dataSource?: string;
  fetchedAt?: string;
  error?: string;
}

export async function fetchLiveScores(staticGenerated: string): Promise<LiveScoresPayload | null> {
  try {
    const res = await fetch("/api/scores", { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as LiveScoresPayload;
    if (data.error || !data.skills?.length) return null;
    const isNewerThanStatic = Boolean(
      data.generated && staticGenerated && data.generated > staticGenerated
    );
    return { ...data, isNewerThanStatic } as LiveScoresPayload & { isNewerThanStatic?: boolean };
  } catch {
    return null;
  }
}

export async function fetchLiveSnapshot(): Promise<PortfolioSnapshot | null> {
  try {
    const res = await fetch("/api/snapshots", { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { latest?: PortfolioSnapshot | null; error?: string };
    if (data.error) return null;
    return data.latest ?? null;
  } catch {
    return null;
  }
}